import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'
import { BlogCard } from '@/components/blog/BlogCard'
import { BlogLayout } from '@/components/blog/BlogLayout'
import { SEO } from '@/components/seo/SEO'
import { findTagBySlug, getPostsByTag } from '@/lib/blog'

export default function BlogTag() {
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const { tag: tagSlug } = useParams<{ tag: string }>()
  const tag = useMemo(
    () => (tagSlug ? findTagBySlug(tagSlug, language) : undefined),
    [tagSlug, language],
  )
  const posts = useMemo(() => (tag ? getPostsByTag(tag, language) : []), [tag, language])

  if (!tag) {
    return <Navigate to="/blog" replace />
  }

  return (
    <>
      <SEO
        title={t('blog.tagPage.seoTitle', { tag })}
        description={t('blog.tagPage.seoDescription', { tag })}
        canonical={`/blog/tag/${tagSlug}`}
      />
      <BlogLayout showBackToBlog>
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
          <p className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">
            {t('blog.tagPage.label')}
          </p>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">#{tag}</h1>
          <p className="text-slate-600">
            {t('blog.tagPage.count', { count: posts.length })}
          </p>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {posts.length === 0 ? (
            <p className="text-slate-500">{t('blog.empty')}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>
          )}
        </section>
      </BlogLayout>
    </>
  )
}
